import React, { type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { Box, xcss } from '@atlaskit/primitives';
import TagIcon from '@atlaskit/icon/core/migration/tag--label';
import { BitbucketRepoEditableAttribute } from '@atlassian/jira-servicedesk-insight-inline-editable-attributes/src/ui/bitbucket-repo-attribute/index.tsx';
import { BooleanEditableAttribute } from '@atlassian/jira-servicedesk-insight-inline-editable-attributes/src/ui/boolean-attribute/index.tsx';
import { DateEditableAttribute } from '@atlassian/jira-servicedesk-insight-inline-editable-attributes/src/ui/date-attribute/index.tsx';
import { DateTimeEditableAttribute } from '@atlassian/jira-servicedesk-insight-inline-editable-attributes/src/ui/date-time-attribute/index.tsx';
import { GroupEditableAttribute } from '@atlassian/jira-servicedesk-insight-inline-editable-attributes/src/ui/group-attribute/index.tsx';
import { OpsgenieTeamsAttribute } from '@atlassian/jira-servicedesk-insight-inline-editable-attributes/src/ui/opsgenie-team-attribute/index.tsx';
import { ProjectEditableAttribute } from '@atlassian/jira-servicedesk-insight-inline-editable-attributes/src/ui/project-attribute/index.tsx';
import { ObjectEditableAttribute } from '@atlassian/jira-servicedesk-insight-inline-editable-attributes/src/ui/referenced-object-attribute/index.tsx';
import { SelectEditableAttribute } from '@atlassian/jira-servicedesk-insight-inline-editable-attributes/src/ui/select-attribute/index.tsx';
import { StatusEditableAttribute } from '@atlassian/jira-servicedesk-insight-inline-editable-attributes/src/ui/status-attribute/index.tsx';
import { TextAreaEditableAttribute } from '@atlassian/jira-servicedesk-insight-inline-editable-attributes/src/ui/text-area-attribute/index.tsx';
import { TextEditableAttribute } from '@atlassian/jira-servicedesk-insight-inline-editable-attributes/src/ui/text-attribute/index.tsx';
import { UserEditableAttribute } from '@atlassian/jira-servicedesk-insight-inline-editable-attributes/src/ui/user-attribute/index.tsx';
import {
	useObjectStoreState,
	useCanEditObject,
	useIsRestrictedSchema,
} from '@atlassian/jira-servicedesk-insight-object-details-store/src/controllers/store/index.tsx';
import { useObjectSchemaPropertyService } from '@atlassian/jira-servicedesk-insight-object-services/src/services/object-schema-property/index.tsx';
import type {
	InsightObjectAttributeType,
	DefaultAttributeType,
} from '@atlassian/jira-servicedesk-insight-object-types/src/common/types/object-attribute-type.tsx';

import {
	matchInsightObjectAttributeType,
	matchDefaultAttributeSubType,
} from '@atlassian/jira-servicedesk-insight-object-types/src/common/utils/type-matchers/index.tsx';

import { toSchemaId } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { fg } from '@atlassian/jira-feature-gating';
import { EditableAttribute } from './editable-attribute/index.tsx';
import { EmptyAttributesInformation } from './empty-attributes-information/index.tsx';
import { RestrictedSchemaEditingInformation } from './restricted-schema-editing-information/index.tsx';
import { AttributesSkeleton } from './skeleton/index.tsx';
import type { Props } from './types.tsx';
import { isMultiSelectAttribute, isTextAreaAttribute } from './utils.tsx';

// The number of "system attributes" that are always present (Key, Created, Updated) but not displayed in the attributes list
const NUM_SYSTEM_ATTRIBUTES = 3;

export const EditableAttributeList = (props: Props) => {
	const { attributeTypes } = props;
	const [canEdit] = useCanEditObject();

	const [{ objectDetails, hideEmptyAttributes }] = useObjectStoreState();
	const [isRestrictedSchema] = useIsRestrictedSchema();
	const { objectType, attributes = [] } = objectDetails ?? {};
	const { objectSchemaId } = objectType ?? {};

	const [propertyState] = useObjectSchemaPropertyService({
		objectSchemaId: toSchemaId(`${objectSchemaId}`),
	});

	const numHiddenAttributes = hideEmptyAttributes
		? attributeTypes.length - attributes.length + NUM_SYSTEM_ATTRIBUTES
		: 0;

	if (attributeTypes.length === 0) {
		return <AttributesSkeleton />;
	}

	const renderDefaultAttributeType = (defaultAT: DefaultAttributeType) => {
		const renderTextAttribute = () => (
			<TextEditableAttribute attributeType={defaultAT} isEditable={canEdit} />
		);
		return matchDefaultAttributeSubType(defaultAT.defaultType, {
			url: renderTextAttribute,
			email: renderTextAttribute,
			float: renderTextAttribute,
			integer: renderTextAttribute,
			ipAddress: renderTextAttribute,
			text: renderTextAttribute,
			textarea: () => <TextAreaEditableAttribute attributeType={defaultAT} isEditable={canEdit} />,
			select: () => <SelectEditableAttribute attributeType={defaultAT} isEditable={canEdit} />,
			boolean: () => <BooleanEditableAttribute attributeType={defaultAT} isEditable={canEdit} />,
			date: () => <DateEditableAttribute attributeType={defaultAT} isEditable={canEdit} />,
			datetime: () => <DateTimeEditableAttribute attributeType={defaultAT} isEditable={canEdit} />,
		});
	};
	const renderInlineEditableAttribute = (attributeType: InsightObjectAttributeType) =>
		matchInsightObjectAttributeType(attributeType, {
			default: renderDefaultAttributeType,
			object: (objectAT) => (
				<ObjectEditableAttribute
					attributeType={objectAT}
					isEditable={canEdit}
					quickCreateObjects={propertyState.data?.quickCreateObjects}
				/>
			),
			user: (userAT) => <UserEditableAttribute attributeType={userAT} isEditable={canEdit} />,
			group: (groupAT) => <GroupEditableAttribute attributeType={groupAT} isEditable={canEdit} />,
			project: (projectAT) => (
				<ProjectEditableAttribute attributeType={projectAT} isEditable={canEdit} />
			),
			status: (statusAT) => (
				<StatusEditableAttribute attributeType={statusAT} isEditable={canEdit} />
			),
			bitbucketRepo: (bitbucketRepoAT) => (
				<BitbucketRepoEditableAttribute attributeType={bitbucketRepoAT} isEditable={canEdit} />
			),
			opsgenieTeam: (opsgenieTeamAT) => (
				<OpsgenieTeamsAttribute attributeType={opsgenieTeamAT} isEditable={false} />
			),
		});

	return (
		<Container data-testid="servicedesk-insight-editable-attribute-list.ui.attributes-list">
			{isRestrictedSchema && <RestrictedSchemaEditingInformation />}
			{hideEmptyAttributes && numHiddenAttributes > 0 && (
				<EmptyAttributesInformation numHiddenAttributes={numHiddenAttributes} />
			)}
			{attributeTypes.map((attributeType: InsightObjectAttributeType) => {
				const attributeLabel = attributeType.name;
				const isObjectLabel = attributeType.label === true;
				const isMultiSelect = isMultiSelectAttribute(attributeType);

				const isColumn = isTextAreaAttribute(attributeType) || !props.isMultiColumn;
				return (
					<EditableAttribute
						key={attributeLabel}
						label={attributeLabel}
						icon={isObjectLabel ? <TagIcon spacing="spacious" label="" /> : null}
						isColumn={isColumn}
						isMultiSelect={isMultiSelect}
						attributeTypeId={attributeType.id}
					>
						{renderInlineEditableAttribute(attributeType)}
					</EditableAttribute>
				);
			})}
		</Container>
	);
};

const ContainerDl = ({ children }: { children?: ReactNode }) => (
	<Box
		as="dl"
		xcss={containerStyles}
		testId="servicedesk-insight-editable-attribute-list.ui.attributes-list"
	>
		{children}
	</Box>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContainerDiv = styled.div({
	width: 'auto',
});

const Container = componentWithCondition(() => fg('assets-a11y-fix'), ContainerDl, ContainerDiv);

const containerStyles = xcss({ width: 'auto', marginLeft: '0', paddingLeft: '0' });
